import { Input } from '@shadcn/ui/components/ui/input';
import { Popover, PopoverContent, PopoverAnchor } from '@shadcn/ui/components/ui/popover';
import { Avatar, AvatarImage, AvatarFallback } from '@shadcn/ui/components/ui/avatar';
import { Button } from '@shadcn/ui/components/ui/button';
import { Loader2, Sparkles, User, SendHorizonal, Search, BookOpen } from 'lucide-react';
import { useAssistantRuntime } from '@assistant-ui/react';
import { useRef, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useQuery } from '@apollo/client';
import { SEARCH_CONTACT_PROFILES, GET_GROUPS } from '@zaplify/graphql';
import { useGqlQueryVariables } from '../hooks/use-gql-query-variables';
import { useIntentBasedFilters, type SuggestionId } from '../hooks/use-intent-based-filters';
import { cn } from '@shadcn/ui';
import { AndSendLoader } from '@shadcn/ui/components/icons/and-send-loader/andsend-loader';

type SuggestionType = 'agent-prompt' | 'profile' | 'free-text-contact' | 'playbook';

interface BaseSuggestion {
    id: string;
    type: SuggestionType;
    name: string;
    subtitle?: string;
}

interface AgentPromptSuggestion extends BaseSuggestion {
    id: SuggestionId;
    type: 'agent-prompt';
    prompt: string;
}

interface DynamicAgentPromptSuggestion extends BaseSuggestion {
    id: string;
    type: 'agent-prompt';
    prompt: string;
}

interface ProfileSuggestion extends BaseSuggestion {
    type: 'profile';
    avatar?: string;
    title?: string;
}

interface FreeTextContactSuggestion extends BaseSuggestion {
    type: 'free-text-contact';
    searchTerm: string;
}

interface PlaybookSuggestion extends BaseSuggestion {
    type: 'playbook';
    avatar?: string;
    isDefault?: boolean;
}

type AutocompleteSuggestion =
    | AgentPromptSuggestion
    | DynamicAgentPromptSuggestion
    | ProfileSuggestion
    | FreeTextContactSuggestion
    | PlaybookSuggestion;

const SuggestionItem = ({
    suggestion,
    onSelect,
}: {
    suggestion: AutocompleteSuggestion;
    onSelect: (suggestion: AutocompleteSuggestion) => void;
}) => {
    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((word) => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const renderIcon = () => {
        switch (suggestion.type) {
            case 'agent-prompt':
                return <Sparkles className="h-4 w-4 text-purple-500 mr-2 ml-2" />;
            case 'profile':
                return (
                    <Avatar className="h-8 w-8">
                        {suggestion.avatar ? <AvatarImage src={suggestion.avatar} alt={suggestion.name} /> : null}
                        <AvatarFallback className="text-xs">{getInitials(suggestion.name)}</AvatarFallback>
                    </Avatar>
                );
            case 'free-text-contact':
                return <Search className="h-4 w-4 text-text-tertiary mr-2 ml-2" />;
            case 'playbook':
                return (
                    <Avatar className="h-8 w-8">
                        {suggestion.avatar ? <AvatarImage src={suggestion.avatar} alt={suggestion.name} /> : null}
                        <AvatarFallback className="text-xs">
                            <BookOpen className="h-4 w-4" />
                        </AvatarFallback>
                    </Avatar>
                );
            default:
                return <User className="h-4 w-4" />;
        }
    };

    return (
        <div
            className="flex items-center gap-3 px-4 py-3 hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
            onClick={() => onSelect(suggestion)}
        >
            {renderIcon()}
            <div className="flex-1 min-w-0">
                <div
                    className={cn(
                        'font-medium text-sm',
                        suggestion.type === 'agent-prompt' && 'font-normal',
                        suggestion.type !== 'free-text-contact' && suggestion.type !== 'agent-prompt' && 'capitalize'
                    )}
                >
                    {suggestion.name}
                    {suggestion.type === 'playbook' && (suggestion as PlaybookSuggestion).isDefault && (
                        <span className="text-xs text-muted-foreground ml-2">(Default)</span>
                    )}
                </div>
                {suggestion.subtitle && (
                    <div className="text-xs text-muted-foreground truncate">{suggestion.subtitle}</div>
                )}
            </div>
        </div>
    );
};

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

export const ActionAgentPromptInput = () => {
    const { thread } = useAssistantRuntime();
    const {
        setFilterContactIds,
        setTextSearchQuery,
        setFilterGroupIds,
        setGqlQueryVariables,
        setActiveFilterId,
        setUserRequest,
        setError,
    } = useGqlQueryVariables();
    const { getFilterForSuggestion } = useIntentBasedFilters();
    const [value, setValue] = useState('');
    const [searchParams, setSearchParams] = useSearchParams();
    const threadId = searchParams.get('threadId') || undefined;
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Track if the last pointer/mouse event started inside the popover. This helps prevent
    // closing the popover on Windows when the user clicks the scrollbar or any element
    // that doesn't move focus (relatedTarget can be null).
    const mouseDownInsidePopoverRef = useRef(false);

    // Debounce the search value with a 300ms delay
    const debouncedSearchValue = useDebounce(value, 300);

    // Search for contacts and playbooks with the current value (no @ prefix needed)
    const searchTerm = value.trim();
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    const { data: contactProfiles, loading: contactProfilesLoading } = useQuery(SEARCH_CONTACT_PROFILES, {
        variables: {
            limit: 5,
            search: debouncedSearchTerm ? `%${debouncedSearchTerm}%` : '%',
        },
    });

    const { data: groupsData, loading: groupsLoading } = useQuery(GET_GROUPS);

    // Autocomplete state
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState<AutocompleteSuggestion[]>([]);
    const [hasBeenShown, setHasBeenShown] = useState(false);
    const [hasShownEmptyResults, setHasShownEmptyResults] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Top 5 intent-based agent prompts for relationship-based sales
    const intentBasedPrompts: AutocompleteSuggestion[] = [
        {
            id: 'urgent-followups',
            type: 'agent-prompt',
            name: 'Contacts waiting for my reply',
            prompt: 'Show contacts who are waiting for my response',
        },
        {
            id: 'hot-leads',
            type: 'agent-prompt',
            name: 'Prospects with good timing',
            prompt: 'Show contacts with high buying intent',
        },
        {
            id: 'dormant-customers',
            type: 'agent-prompt',
            name: 'Customers to re-engage',
            prompt: "Show customers I haven't engaged with recently",
        },
        {
            id: 'high-value-prospects',
            type: 'agent-prompt',
            name: 'High-value opportunities',
            prompt: 'Show prospects with high business value',
        },
        {
            id: 'churn-risk-customers',
            type: 'agent-prompt',
            name: 'Customers at risk of leaving',
            prompt: 'Show customers who have churn risk identified',
        },
    ];

    // Convert real contact data to ProfileSuggestion format
    const getContactSuggestions = (): ProfileSuggestion[] => {
        if (!contactProfiles?.Contacts) return [];

        return contactProfiles.Contacts.map(
            (contact): ProfileSuggestion => ({
                id: contact.id,
                type: 'profile',
                name:
                    contact.fullName ||
                    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
                    'Unknown Contact',
                avatar: contact.linkedinProfileImgUrl || undefined,
                title: contact.occupationTitle || undefined,
                subtitle:
                    [contact.occupationTitle, contact.organizationName]
                        .filter(Boolean)
                        .map((item) =>
                            item
                                .toLowerCase()
                                .split(' ')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')
                        )
                        .join(' at ') || undefined,
            })
        );
    };

    // Convert groups data to PlaybookSuggestion format
    const getPlaybookSuggestions = (): PlaybookSuggestion[] => {
        if (!groupsData?.Groups) return [];

        return groupsData.Groups.filter((group) => {
            if (!searchTerm) return true;
            return group.name.toLowerCase().includes(searchTerm.toLowerCase());
        }).map(
            (group): PlaybookSuggestion => ({
                id: group.id,
                type: 'playbook',
                name: group.name,
                avatar: group.imgSrc || undefined,
                isDefault: group.default,
                subtitle: 'Playbook',
            })
        );
    };

    // Update suggestions when contact profiles and groups finish loading and input is empty and focused
    useEffect(() => {
        if (
            !contactProfilesLoading &&
            !groupsLoading &&
            !value.trim() &&
            document.activeElement === inputRef.current &&
            isOpen
        ) {
            // Show agent prompts when input is empty
            setSuggestions(intentBasedPrompts);
            setIsLoading(false);
        }
    }, [contactProfilesLoading, contactProfiles, groupsLoading, groupsData, value, isOpen]);

    // Handle suggestions when value or contact data changes
    useEffect(() => {
        // Only process when input is focused
        if (document.activeElement !== inputRef.current) {
            return;
        }

        if (!value.trim()) {
            // Show agent prompts when focused but no search term
            setSuggestions(intentBasedPrompts);
            setIsLoading(false);
            setHasBeenShown(true);
            setHasShownEmptyResults(false);
            setIsOpen(true);
            return;
        }

        // Handle search (always search mode now)
        const isWaitingForSearchDebounce = searchTerm !== debouncedSearchTerm;
        setIsLoading(
            contactProfilesLoading ||
                groupsLoading ||
                isWaitingForSearchDebounce ||
                (hasBeenShown && !hasShownEmptyResults)
        );

        // Don't proceed if still loading contact data or groups data or waiting for debounce
        if (contactProfilesLoading || groupsLoading || isWaitingForSearchDebounce) {
            return;
        }

        // Get both playbook and contact suggestions
        const playbookSuggestions = getPlaybookSuggestions();
        const contactSuggestions = getContactSuggestions();

        // Filter agent prompts based on search term
        const filteredAgentPrompts = intentBasedPrompts.filter(
            (suggestion) =>
                suggestion.name.toLowerCase().includes(value.toLowerCase()) ||
                (suggestion.type === 'agent-prompt' && suggestion.prompt.toLowerCase().includes(value.toLowerCase()))
        );

        // Create suggestions array with free text suggestion at the top if there's a search term
        let allSuggestions: AutocompleteSuggestion[] = [];

        // Add free text suggestion if there's a search term
        if (searchTerm.trim()) {
            const freeTextSuggestion: FreeTextContactSuggestion = {
                id: `free-text-${searchTerm}`,
                type: 'free-text-contact',
                name: searchTerm,
                searchTerm: searchTerm,
                subtitle: 'Search among names, titles, and companies',
            };
            allSuggestions.push(freeTextSuggestion);

            // Add dynamic agent prompt suggestion using the typed value
            const dynamicAgentPrompt: DynamicAgentPromptSuggestion = {
                id: `dynamic-prompt-${searchTerm}`,
                type: 'agent-prompt',
                name: searchTerm,
                prompt: searchTerm,
                subtitle: 'Ask Andy to find this for you',
            };
            allSuggestions.push(dynamicAgentPrompt);
        }

        // Add agent prompts next (filtered by search term)
        allSuggestions.push(...filteredAgentPrompts);

        // Add playbook suggestions
        allSuggestions.push(...playbookSuggestions);

        // Add contact profile suggestions last
        allSuggestions.push(...contactSuggestions);

        setSuggestions(allSuggestions);
        setIsLoading(false);

        if (allSuggestions.length > 0) {
            setHasBeenShown(true);
            setHasShownEmptyResults(false);
            setIsOpen(true);
        } else if (hasBeenShown) {
            setHasShownEmptyResults(true);
            setIsOpen(false);
        } else {
            setIsOpen(false);
        }
    }, [
        value,
        debouncedSearchValue,
        debouncedSearchTerm,
        searchTerm,
        hasBeenShown,
        hasShownEmptyResults,
        contactProfilesLoading,
        contactProfiles,
        groupsLoading,
        groupsData,
    ]);

    // Reset highlighted index when suggestions change
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [suggestions]);

    // Reset highlighted index when popover opens/closes
    useEffect(() => {
        if (!isOpen) {
            setHighlightedIndex(-1);
        }
    }, [isOpen]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (blurTimeoutRef.current) {
                clearTimeout(blurTimeoutRef.current);
            }
        };
    }, []);

    const handleSend = () => {
        if (value.trim()) {
            // Treat any input as a free text search
            setTextSearchQuery(value.trim());
            setValue('');
            setIsOpen(false);
            setHighlightedIndex(-1);
            inputRef.current?.blur();
        } else {
            // Navigate to chat when no value (like clicking agent prompt button)
            setIsOpen(false);
            setHighlightedIndex(-1);
            inputRef.current?.blur();

            // Navigate to chat
            navigate({
                pathname: '/new/actions-v2/chat',
                search: threadId ? '?threadId=' + threadId : '',
            });
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            // If a suggestion is highlighted, select it
            if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
                handleSuggestionSelect(suggestions[highlightedIndex]);
            } else {
                handleSend();
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
            setHighlightedIndex(-1);
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (isOpen && suggestions.length > 0) {
                setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
            }
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (isOpen && suggestions.length > 0) {
                setHighlightedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
            }
        }
    };

    const handleFocus = () => {
        // Clear any pending close timeout
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
            blurTimeoutRef.current = null;
        }

        // If input is empty, immediately show agent prompts
        if (!value.trim()) {
            setSuggestions(intentBasedPrompts);
            setIsLoading(false);
            setHasBeenShown(true);
            setHasShownEmptyResults(false);
            setIsOpen(true);
            return;
        }

        // Show popover if we have suggestions or it has been shown before
        if (suggestions.length > 0 || hasBeenShown) {
            setIsOpen(true);
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        // Clear any existing timeout
        if (blurTimeoutRef.current) {
            clearTimeout(blurTimeoutRef.current);
        }

        // If the most recent mousedown started inside the popover, we should not close it.
        if (mouseDownInsidePopoverRef.current) {
            // Reset the flag for the next interaction cycle
            mouseDownInsidePopoverRef.current = false;
            return;
        }

        // Don't close if focus is moving to the popover content
        const relatedTarget = e.relatedTarget as Element | null;
        if (
            relatedTarget &&
            (relatedTarget.closest('[data-radix-popover-content]') || containerRef.current?.contains(relatedTarget))
        ) {
            return;
        }

        // Delay closing to allow clicking on suggestions
        blurTimeoutRef.current = setTimeout(() => {
            // Double check that we should still close (user might have refocused)
            if (document.activeElement !== inputRef.current) {
                setIsOpen(false);
                // Reset states when popover closes
                if (!value.trim()) {
                    setHasBeenShown(false);
                    setHasShownEmptyResults(false);
                }
            }
            blurTimeoutRef.current = null;
        }, 150);
    };

    const handleSuggestionSelect = (suggestion: AutocompleteSuggestion) => {
        if (suggestion.type === 'profile' && suggestion.id) {
            setFilterContactIds([suggestion.id]);
            setValue('');
            setIsOpen(false);
            setHighlightedIndex(-1);
            inputRef.current?.blur();
            return;
        }
        if (suggestion.type === 'free-text-contact') {
            setTextSearchQuery(suggestion.searchTerm);
            setValue('');
            setIsOpen(false);
            setHighlightedIndex(-1);
            inputRef.current?.blur();
            return;
        }
        if (suggestion.type === 'playbook' && suggestion.id) {
            setFilterGroupIds([suggestion.id]);
            setValue('');
            setIsOpen(false);
            setHighlightedIndex(-1);
            inputRef.current?.blur();
            return;
        }
        if (suggestion.type === 'agent-prompt') {
            // Check if this is a predefined agent prompt with a SuggestionId
            const predefinedSuggestionIds: SuggestionId[] = [
                'urgent-followups',
                'hot-leads',
                'dormant-customers',
                'high-value-prospects',
                'churn-risk-customers',
            ];

            if (predefinedSuggestionIds.includes(suggestion.id as SuggestionId)) {
                // Apply intent-based filter for predefined prompts
                const intentFilter = getFilterForSuggestion(suggestion.id as SuggestionId);

                // Apply the filter directly using the same pattern as the display action tool
                const filterId = `agent-prompt-${suggestion.id}`;
                setGqlQueryVariables(intentFilter);
                setActiveFilterId(filterId);
                setUserRequest(suggestion.name);
                setError(undefined);
            }

            // Send message to thread and navigate to chat for all agent prompts (predefined and dynamic)
            thread.append(suggestion.name);

            setValue('');
            setIsOpen(false);
            setHighlightedIndex(-1);
            inputRef.current?.blur();

            // Navigate to chat
            navigate({
                pathname: '/new/actions-v2/chat',
                search: threadId ? '?threadId=' + threadId : '',
            });
            return;
        } else {
            setValue(suggestion.name);
        }
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.focus();
    };

    return (
        <div ref={containerRef} className="w-full">
            <Popover open={isOpen} modal={false}>
                <PopoverAnchor asChild>
                    <div className="relative flex gap-2 w-full py-1 bg-background-tertiary rounded-xl items-center px-2 pr-11 border">
                        <Input
                            className="border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                            ref={inputRef}
                            placeholder="Find in your network..."
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <Button
                            className="absolute right-0 rounded-r-xl py-2 px-4 flex items-center h-full border-l rounded-l-none"
                            onClick={handleSend}
                        >
                            <AnimatePresence mode="wait">
                                {!value.trim() ? (
                                    <motion.div
                                        key="bot"
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0, opacity: 0 }}
                                        transition={{ duration: 0.1, ease: 'easeInOut' }}
                                    >
                                        <AndSendLoader
                                            start={1}
                                            end={80}
                                            loop={false}
                                            variation="white"
                                            className="h-4 w-4"
                                        />
                                        {/* <img
                                            src="/images/logos/andsend-icon-striped-color.png"
                                            alt="Andsend"
                                            className="h-5 w-5 filter brightness-0 invert"
                                        /> */}
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="send"
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0, opacity: 0 }}
                                        transition={{ duration: 0.1, ease: 'easeInOut' }}
                                    >
                                        <Search className="h-4 w-4 text-white" />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Button>
                    </div>
                </PopoverAnchor>

                <PopoverContent
                    ref={popoverRef}
                    className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[300px] overflow-y-auto"
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    // Mark that the pointer interaction began inside the popover so the
                    // upcoming blur event on the input doesn't close the popover prematurely
                    onMouseDown={() => {
                        mouseDownInsidePopoverRef.current = true;
                    }}
                    onPointerDown={() => {
                        // Also cover other pointer events (e.g., touch).
                        mouseDownInsidePopoverRef.current = true;
                    }}
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center p-4">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            <span className="text-sm text-muted-foreground">Searching...</span>
                        </div>
                    ) : suggestions.length > 0 ? (
                        <div>
                            <div className="py-2">
                                {suggestions.map((suggestion, index) => (
                                    <div
                                        key={suggestion.id}
                                        className={`${
                                            index === highlightedIndex ? 'bg-accent text-accent-foreground' : ''
                                        }`}
                                    >
                                        <SuggestionItem suggestion={suggestion} onSelect={handleSuggestionSelect} />
                                    </div>
                                ))}
                            </div>
                            {!value.trim() && (
                                <div className="px-4 py-2 border-t border-border">
                                    <span className="text-xs text-muted-foreground">
                                        Start typing to search for playbooks, people, and more
                                    </span>
                                </div>
                            )}
                        </div>
                    ) : null}
                </PopoverContent>
            </Popover>
        </div>
    );
};
